import { PartySocket } from 'partysocket';
import { Game } from '../game/simulation.js';
import { DT, BY_ID } from '../game/roster.js';
import { PROTOCOL, CLOSE, terminalClose } from '../game/protocol.js';
import { moveActor } from '../game/map.js';
import { clamp, lerp } from '../game/math.js';

export const makeCode=()=>Array.from(crypto.getRandomValues(new Uint8Array(8)),n=>'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'[n%32]).join('');
export class LocalSession {
  online=false;id='local';epoch=0;seq=0;accumulator=0;paused=false;pulses={fire:false,super:false,gadget:false};
  constructor({character,mode,name,difficulty='normal'}) {
    this.game=new Game({mode,seed:crypto.getRandomValues(new Uint32Array(1))[0],difficulty});
    this.game.addPlayer(this.id,character,name);this.game.start();this.current=this.game.snapshot(this.id);this.current.me=this.id;
  }
  update(dt,input) {
    for(const key of ['fire','super','gadget'])this.pulses[key]||=input[key];
    if(!this.paused){this.accumulator+=Math.min(dt,.1);while(this.accumulator>=DT){this.game.setInput(this.id,{...input,...this.pulses,seq:++this.seq});this.game.step(DT);this.pulses={fire:input.fire,super:false,gadget:false};this.accumulator-=DT;input={...input,super:false,gadget:false};}}
    this.current=this.game.snapshot(this.id);this.current.me=this.id;this.current.epoch=0;return this.current;
  }
  close(){}
}

/** Reconnecting transport. Inputs are intentions; positions and damage are never sent. */
export class NetworkSession {
  online=true;id=null;seq=0;current=null;previous=null;accumulator=0;pending=[];status='Connecting';rtt=0;closed=false;epoch=-1;received=0;pulses={fire:false,super:false,gadget:false};
  constructor({room,character,mode,name,onStatus=()=>{},onError=()=>{},onEpoch=()=>{},host=globalThis.location?.host,Socket=PartySocket}) {
    this.room=room;this.onStatus=onStatus;this.onError=onError;this.onEpoch=onEpoch;this.join={type:'join',version:PROTOCOL,character,mode,name};
    let token;try{token=sessionStorage.getItem('holiduel-session-'+room);if(!token){token=crypto.randomUUID();sessionStorage.setItem('holiduel-session-'+room,token);}}catch{token=crypto.randomUUID();}
    // The injectable constructor lets tests exercise transport lifecycle without a browser or network.
    this.socket=new Socket({host,party:'arena',room,query:{session:token},maxEnqueuedMessages:0,connectionTimeout:8000,maxRetries:12});
    this.socket.addEventListener('open',()=>{if(this.closed)return;this.clearInputs();this.status='Connected';this.send(this.join);this.onStatus(this.status);});
    this.socket.addEventListener('close',event=>{
      if(this.closed)return;
      this.clearInputs();
      if(terminalClose(event.code)){
        this.status=event.code===CLOSE.REPLACED?'Session moved':event.code===CLOSE.IDLE?'Room expired':'Unable to join';
        // PartySocket retries remote closes by default. Explicitly stop terminal closes,
        // especially session replacement, or duplicated tabs can steal the player forever.
        this.close();this.onStatus(this.status);
        this.onError(event.code===CLOSE.REPLACED?'This fighter is now controlled in another tab. Leave this party to play separately.':event.reason||'Unable to join this room.');
      }else{this.status='Reconnecting';this.onStatus(this.status);}
    });
    this.socket.addEventListener('error',()=>{if(!this.closed){this.clearInputs();this.status='Reconnecting';this.onStatus(this.status);}});
    this.socket.addEventListener('message',event=>this.receive(event.data));
    this.pinger=setInterval(()=>this.send({type:'ping',at:Date.now()}),2500);
    this.joinTimeout=setTimeout(()=>{if(!this.current&&!this.closed)this.onError('Room connection is taking longer than expected. The multiplayer Worker must be running.');},11000);
  }
  clearInputs(){this.pending=[];this.accumulator=0;this.pulses={fire:false,super:false,gadget:false};}
  receive(raw) {
    if(this.closed)return;
    let state;try{state=JSON.parse(raw);}catch{return;}
    if(!state||typeof state!=='object')return;
    if(state.type==='welcome'){this.id=state.id;return;}
    if(state.type==='error'){this.onError(state.message);return;}
    if(state.type==='pong'){this.rtt=Math.max(0,Date.now()-state.at);return;}
    if(state.type!=='state'||!Array.isArray(state.players)||!Array.isArray(state.roster))return;
    clearTimeout(this.joinTimeout);this.id=state.me||this.id;
    if(state.epoch!==this.epoch){this.epoch=state.epoch;this.clearInputs();this.previous=null;this.onEpoch(this.epoch);}
    else this.previous=this.current;
    const me=state.players.find(p=>p.id===this.id);if(me){this.seq=Math.max(this.seq,me.seq||0);this.pending=this.pending.filter(p=>p.seq>me.seq);}
    this.current=state;this.received=performance.now();this.status='Connected';
  }
  send(message) {if(!this.closed&&this.socket.readyState===1)this.socket.send(JSON.stringify(message));}
  update(dt,input) {
    const connected=!this.closed&&this.status==='Connected'&&this.socket.readyState===1&&this.current?.phase==='playing';
    if(connected){
      for(const key of ['fire','super','gadget'])this.pulses[key]||=input[key];
      this.accumulator+=Math.min(dt,.1);
      if(this.accumulator>=DT){this.accumulator%=DT;const packet={type:'input',...input,...this.pulses,seq:++this.seq};this.pulses={fire:false,super:false,gadget:false};this.send(packet);this.pending.push({...packet,dt:DT});if(this.pending.length>30)this.pending.shift();}
    }else this.clearInputs();
    if(!this.current)return null;
    const state={...this.current,players:this.current.players.map(p=>({...p}))},age=(performance.now()-this.received)/1000;
    const alpha=clamp(age/(2*DT),0,1);
    for(const p of state.players){
      const previous=this.previous?.players.find(q=>q.id===p.id);
      if(p.id!==this.id&&previous&&p.hp>0&&previous.hp>0&&Math.hypot(p.x-previous.x,p.z-previous.z)<4){p.x=lerp(previous.x,p.x,alpha);p.z=lerp(previous.z,p.z,alpha);p.y=lerp(previous.y,p.y,alpha);}
      // Reapply unacknowledged movement against the same collision map, then reconcile on each snapshot.
      // Ability motion is deliberately not predicted: authoritative dash/leap positions take precedence.
      if(connected&&p.id===this.id&&p.hp>0&&p.stun<=0&&!p.motion&&p.y<.1&&age<.5){
        const c=BY_ID[p.character],speed=c.speed*(p.haste>0?1.4:1)*(p.overdrive>0?1.22:1)*(p.slowTime>0?1-clamp(p.slow??.35,0,1):1);
        for(const move of this.pending)moveActor(p,move.x*speed*move.dt,move.z*speed*move.dt,state.map);
        p.ax=input.ax;p.az=input.az;
      }
    }
    return state;
  }
  close(){if(this.closed)return;this.closed=true;clearInterval(this.pinger);clearTimeout(this.joinTimeout);this.clearInputs();this.socket.close();}
}
